import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import HighlightText from '../WidgetEditComponents/HighlightText';
import AIRefreshIcon from '../../components/AIRefreshIcon';

function appendPercent(value) {
    if (typeof value === 'string' && value.includes('%')) {
        return value;
    }
    return `${value}%`;
}

function SimplisticHeader({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const defaultBackgroundImage = '/img/Background_Images.webp';
    const deleteLogo = '/img/delete.svg';

    const fonts = useFont();
    
    // Inline Editing States
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    
    // Image Upload States
    const [isUploadingBg, setIsUploadingBg] = useState(false);
    
    // Animation Trigger State (2025 Animation System)
    const [animationKey, setAnimationKey] = useState(0);
    
    // Refs für Outside-Click Detection und Container
    const widgetRef = useRef(null);
    const containerRef = useRef(null);

    // Animation Trigger Effect (2025 Animation System)
    useEffect(() => {
        if (data.triggerAnimation && isEditMode) {
            setAnimationKey(prev => prev + 1);
        }
    }, [data.triggerAnimation, isEditMode]);

    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);

    // 🚀 RESPONSIVE LAYOUT STYLES - Revolutionary Layout System
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor || '#ffffff',
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none',
        display: 'flex' // Flexbox für den Haupt-Container
    });

    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Height-Property (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 500)
        })
    };

    // Dynamische Breakpoint-Erkennung basierend auf Container-Breite
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        // Initial check
        checkContainerWidth();

        // Listen for window resize
        window.addEventListener('resize', checkContainerWidth);
        
        // Listen for container size changes (für Panel-Toggles)
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Smart Outside-Click Handler - nur für echte Außenklicks
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            // Smarte Erkennung: Ist es ein echter Außenklick?
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            // Definiere was ein "echter Außenklick" ist
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            console.log('🔍 SimplisticHeader smart outside click analysis:', {
                isInlineEditing,
                target: event.target.tagName,
                targetClass: event.target.className,
                isEditingInput,
                isActionButton,
                isDeleteButton,
                isWidgetEdit,
                isPagePreview,
                isRealOutsideClick,
                inlineValue
            });
            
            if (isRealOutsideClick) {
                console.log('✅ Real outside click confirmed - saving and closing');
                // Speichern und beenden
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
                    onUpdate(updatedData);
                    console.log('💾 Saved data:', updatedData);
                }
                // Edit-Modus beenden
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            console.log('🎯 Adding smart outside click listener for SimplisticHeader field:', isInlineEditing);
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                console.log('🧹 Removing smart outside click listener');
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    const handleButtonClick = () => {
        if (!isEditMode && data.buttonLink) {
            window.open(data.buttonLink, '_blank');
        }
    };

    // Inline Editing Functions
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!isEditMode || !onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        // Toggle functionality: If already editing this field, cancel editing
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = (fieldName) => {
        console.log('💾 SimplisticHeader handleInlineSubmit called:', { fieldName, inlineValue, hasOnUpdate: !!onUpdate });
        
        if (!onUpdate) {
            console.error('❌ No onUpdate function provided');
            return;
        }
        
        if (!fieldName || fieldName !== isInlineEditing) {
            console.error('❌ Invalid fieldName or not currently editing');
            return;
        }
        
        // Update the widget data
        const updatedData = { ...data, [fieldName]: inlineValue };
        console.log('📝 Updating SimplisticHeader data:', updatedData);
        onUpdate(updatedData);
        
        // Reset inline editing state
        setIsInlineEditing(null);
        setInlineValue('');
        console.log('✅ SimplisticHeader inline editing ended');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    // Image Upload Function
    const handleImageUpload = (fieldName, setUploadingState) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file && onUpdate) {
                setUploadingState(true);
                
                // Simuliere Upload (in echter App: Firebase Upload)
                setTimeout(() => {
                    const fakeUrl = URL.createObjectURL(file);
                    const updatedData = { ...data, [fieldName]: fakeUrl };
                    onUpdate(updatedData);
                    setUploadingState(false);
                }, 2000);
            }
        };
        input.click();
    };

    const handleInlineKeyPress = (e, fieldName) => {
        // Dynamische Detection: Ist es gerade ein Textarea?
        const isCurrentlyTextarea = e.target.tagName === 'TEXTAREA';
        
        if (e.key === 'Enter') {
            if (isCurrentlyTextarea) {
                // Textareas: Enter = neue Zeile, Ctrl+Enter = speichern
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    handleInlineSubmit(fieldName);
                }
                // Enter allein = neue Zeile (normales Verhalten)
            } else {
                // Input-Felder: Enter = speichern
                e.preventDefault();
                handleInlineSubmit(fieldName);
            }
        } else if (e.key === 'Escape') {
            handleInlineCancel();
        }
    };

    // Render inline editable text
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            // In Live-Modus: Button-Click-Funktionalität beibehalten
            if (Element === 'button' && currentValue) {
                return (
                    <Element 
                        style={style}
                        onClick={handleButtonClick}
                        onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                        }}
                    >
                        {currentValue}
                    </Element>
                );
            }
            
            return currentValue ? (
                <Element style={{
                    ...style,
                    whiteSpace: style.whiteSpace || 'pre-wrap' // ← Behält Original-whiteSpace bei
                }}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            // Verwende die gespeicherten Original-Layout-Eigenschaften
            const layoutData = originalLayout;
            
            // UNIVERSELL: ALLE Text-Elemente als Textarea, AUSSER Buttons
            const isTextarea = Element !== 'button'; // ← Buttons bleiben Input-Felder
            
            // Action Buttons Container
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: Element === 'button' ? '-11px' : '-11px',
                    right: Element === 'button' ? '-6px' : '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    {/* Grüner Haken - Speichern */}
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#059669';
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.boxShadow = '0 2px 6px rgba(16, 185, 129, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#10b981';
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = '0 1px 3px rgba(16, 185, 129, 0.3)';
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    {/* Rotes X - Abbrechen */}
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#dc2626';
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.boxShadow = '0 2px 6px rgba(239, 68, 68, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#ef4444';
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = '0 1px 3px rgba(239, 68, 68, 0.3)';
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );
            
            return isTextarea ? (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <textarea
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            // ✅ Cursor-Position merken BEVOR wir die Höhe ändern
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                // ✅ Cursor-Position wiederherstellen
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => {
                            handleInlineKeyPress(e, fieldName);
                            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                setTimeout(() => {
                                    const target = e.target;
                                    target.style.height = 'auto';
                                    target.style.height = target.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        ref={(textarea) => {
                            if (textarea) {
                                setTimeout(() => {
                                    const originalHeight = parseInt(layoutData.height) || parseInt(style.fontSize) * 1.6;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = Math.max(textarea.scrollHeight, originalHeight) + 'px';
                                    // ✅ NUR beim ersten Laden ans Ende setzen, nicht bei jedem Re-Render
                                    if (textarea.selectionStart === 0 && textarea.selectionEnd === 0) {
                                        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                                    }
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '4px 8px',
                            margin: '0',
                            width: '100%',
                            minHeight: layoutData.height || 'auto',
                            color: '#333333',
                            backgroundImage: 'none',
                            backgroundClip: 'unset',
                            webkitBackgroundClip: 'unset',
                            webkitTextFillColor: '#333333',
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            lineHeight: style.lineHeight,
                            letterSpacing: style.letterSpacing,
                            textAlign: style.textAlign || 'inherit',
                            whiteSpace: style.whiteSpace || 'pre-wrap' // ← Behält Original-whiteSpace bei
                        }}
                    />
                    {renderActionButtons()}
                </div>
            ) : (
                // BUTTONS: Input-Felder
                <input
                    type="text"
                    value={inlineValue}
                    onChange={(e) => {
                        setInlineValue(e.target.value);
                        // ✅ Cursor-Position wird automatisch beibehalten bei normalen Inputs
                    }}
                    onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                    onBlur={() => handleInlineSubmit(fieldName)}
                    placeholder={placeholder}
                    autoFocus
                    ref={(input) => {
                        if (input) {
                            setTimeout(() => {
                                // ✅ NUR beim ersten Laden ans Ende setzen, nicht bei jedem Re-Render
                                if (input.selectionStart === 0 && input.selectionEnd === 0) {
                                    input.setSelectionRange(input.value.length, input.value.length);
                                }
                            }, 0);
                        }
                    }}
                    style={{
                        ...style,
                        backgroundColor: style.backgroundColor,
                        border: style.border || 'none',
                        borderRadius: style.borderRadius,
                        padding: style.padding,
                        width: layoutData.width || style.width,
                        height: layoutData.height || style.height,
                        minWidth: layoutData.width || style.minWidth,
                        maxWidth: layoutData.width || 'none',
                        minHeight: layoutData.height || 'auto',
                        maxHeight: layoutData.height || 'none',
                        marginTop: layoutData.marginTop || '0',
                        marginBottom: layoutData.marginBottom || '0',
                        marginLeft: layoutData.marginLeft || '0',
                        marginRight: layoutData.marginRight || '0',
                        display: style.display,
                        alignItems: style.alignItems,
                        justifyContent: style.justifyContent,
                        alignSelf: style.alignSelf,
                        boxShadow: 'none',
                        cursor: 'text',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                />
            );
        }

        // Edit-Modus aber nicht gerade editiert: EINFACHE Darstellung ohne störende Effekte
        const handleElementClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const rect = e.target.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(e.target);
            
            const layoutData = {
                width: rect.width + 'px',
                height: rect.height + 'px',
                marginTop: computedStyle.marginTop,
                marginBottom: computedStyle.marginBottom,
                marginLeft: computedStyle.marginLeft,
                marginRight: computedStyle.marginRight
            };
            
            setOriginalLayout(layoutData);
            handleInlineEdit(fieldName, currentValue);
        };

        return (
            <Element 
                style={{
                    ...style,
                    cursor: 'text',
                    whiteSpace: style.whiteSpace || 'pre-wrap' // ← KRITISCH: Original-whiteSpace beibehalten
                }}
                onClick={handleElementClick}
                title="Click to edit"
            >
                {currentValue || placeholder}
            </Element>
        );
    };

    // Layout Configuration basierend auf layoutType
    const getLayoutConfig = () => {
        const layoutType = data.layoutType || 'centered-classic';
        
        switch (layoutType) {
            case 'centered-classic':
                return {
                    iconPosition: 'top',
                    textAlign: 'center',
                    contentDirection: 'column',
                    centerLayout: true,
                    largeTitle: true,
                    compactSubtext: true
                };
            case 'icon-left-text-right':
                return {
                    iconPosition: 'left',
                    textAlign: 'left',
                    textSide: 'right'
                };
            default:
                return {
                    iconPosition: 'top',
                    textAlign: 'center',
                    contentDirection: 'column',
                    centerLayout: true,
                    largeTitle: true,
                    compactSubtext: true
                };
        }
    };

    const layoutConfig = getLayoutConfig();

    // ✅ RESPONSIVE SIZE LOGIC - JavaScript-basiert (funktioniert korrekt!)
    const getResponsiveSize = (baseField) => {
        const isBig = !isMobileLayout && window.innerWidth > 1400;
        
        if (isBig && data[`${baseField}Big`]) {
            return data[`${baseField}Big`];
        } else if (isMobileLayout && data[`${baseField}Mobile`]) {
            return data[`${baseField}Mobile`];
        }
        return data[baseField] || 16;
    };

    // ✅ UPDATED: HeroHeader-kompatible Image Effects (wie im UI angezeigt)
    const getImageEffect = (effect, blurIntensity = 0) => {
        let baseEffect = {};
        
        switch (effect) {
            case 'darken-light':
                baseEffect = { filter: 'brightness(0.8)' };
                break;
            case 'darken':
                baseEffect = { filter: 'brightness(0.6)' };
                break;
            case 'darken-heavy':
                baseEffect = { filter: 'brightness(0.4)' };
                break;
            case 'darken-extreme':
                baseEffect = { filter: 'brightness(0.2)' };
                break;
            case 'lighten-light':
                baseEffect = { filter: 'brightness(1.2)' };
                break;
            case 'lighten':
                baseEffect = { filter: 'brightness(1.4)' };
                break;
            case 'lighten-heavy':
                baseEffect = { filter: 'brightness(1.6)' };
                break;
            case 'lighten-extreme':
                baseEffect = { filter: 'brightness(1.8)' };
                break;
            case 'vignette-light':
                baseEffect = { 
                    filter: 'none',
                    gradient: 'radial-gradient(circle, transparent 60%, rgba(0,0,0,0.2))'
                };
                break;
            case 'vignette':
                baseEffect = { 
                    filter: 'none',
                    gradient: 'radial-gradient(circle, transparent 50%, rgba(0,0,0,0.4))'
                };
                break;
            case 'vignette-heavy':
                baseEffect = { 
                    filter: 'none',
                    gradient: 'radial-gradient(circle, transparent 40%, rgba(0,0,0,0.6))'
                };
                break;
            case 'vignette-extreme':
                baseEffect = { 
                    filter: 'none',
                    gradient: 'radial-gradient(circle, transparent 30%, rgba(0,0,0,0.8))'
                };
                break;
            case 'grayscale':
                baseEffect = { filter: 'grayscale(100%)' };
                break;
            case 'sepia':
                baseEffect = { filter: 'sepia(100%)' };
                break;
            default:
                baseEffect = { filter: 'none', gradient: null };
                break;
        }

        // Blur-Intensität hinzufügen falls vorhanden
        if (blurIntensity > 0) {
            const existingFilter = baseEffect.filter || 'none';
            baseEffect.filter = existingFilter === 'none' 
                ? `blur(${blurIntensity}px)` 
                : `${existingFilter} blur(${blurIntensity}px)`;
        }

        return baseEffect;
    };

    return (
        <>
            {/* CSS Animations */}
            <style>
                {`
                    @keyframes blink {
                        0%, 50% { opacity: 1; }
                        51%, 100% { opacity: 0; }
                    }
                    
                    @keyframes simplicisticHeaderUploadSpin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    @keyframes gradientShift {
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                    }
                    
                    @keyframes gentle-pulse {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.02); opacity: 0.9; }
                    }

                    @keyframes gentle-sway {
                        0%, 100% { transform: translateX(0px); }
                        25% { transform: translateX(-2px); }
                        75% { transform: translateX(2px); }
                    }

                    @keyframes subtle-float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-3px); }
                    }

                    @keyframes gentle-breathe {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.03); opacity: 0.85; }
                    }

                    @keyframes gentle-wobble {
                        0%, 100% { transform: rotate(0deg); }
                        25% { transform: rotate(-1deg); }
                        75% { transform: rotate(1deg); }
                    }
                    
                    @keyframes silverGlow {
                        0% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                        50% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.04) 0%,
                                rgba(192, 192, 192, 0.05) 25%,
                                rgba(255, 255, 255, 0.06) 50%,
                                rgba(192, 192, 192, 0.05) 75%,
                                rgba(255, 255, 255, 0.04) 100%
                            );
                        }
                        100% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                    }
                    
                    /* SimplisticHeader Animation Keyframes (2025 Animation System) */
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: translateX(-30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    
                    @keyframes fadeInSlow {
                        0% { opacity: 0; }
                        30% { opacity: 0; }
                        100% { opacity: 1; }
                    }
                    
                    /* SimplisticHeader Animation Classes */
                    .simplistic-animated {
                        animation-fill-mode: both;
                    }
                    
                    .simplistic-animated.fadeInUp {
                        animation-name: fadeInUp;
                    }
                    
                    .simplistic-animated.slideIn {
                        animation-name: slideIn;
                    }
                    
                    .simplistic-animated.fadeIn {
                        animation-name: fadeIn;
                    }
                    
                    .simplistic-animated.fadeInSlow {
                        animation-name: fadeInSlow;
                    }
                `}
            </style>
            <div 
                className="widget-container simplistic-header-widget"
                style={widgetStyle}
                ref={(el) => {
                    containerRef.current = el;
                    widgetRef.current = el;
                }}
            >
            {/* Delete Button */}
            {isEditMode && (
                <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                    <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                </button>
            )}
            
            {/* 🤖 AI REFRESH ICON */}
            {isEditMode && (
                <AIRefreshIcon 
                    widget={{
                        ...data,
                        type: 'SimplisticHeader'
                    }}
                    spaceId={null}
                    onContentRefreshed={(refreshedWidget) => {
                        console.log('🤖 [SIMPLICISTICHEADER] AI-refreshed content received:', refreshedWidget);
                        onUpdate(refreshedWidget);
                    }}
                    position="top-left"
                />
            )}
            {isEditMode && showDeleteModal && (
                <DeleteModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={(event) => handleDelete(event, data.id)}
                />
            )}
            
            {/* ✅ UPDATED: Background Image Container mit Blur-Rand-Fix */}
            {(data.backgroundImage || data.mobileBackgroundImage) && (() => {
                const blurValue = data.backgroundImageBlur || 0;
                const hasBlur = blurValue > 0;
                const blurOffset = hasBlur ? blurValue * 2 : 0; // Doppelter Blur-Wert als Offset
                
                return (
                    <div style={{
                        position: 'absolute',
                        // ✅ BLUR FIX: Erweitere den Container um den Blur-Offset
                        top: hasBlur ? `-${blurOffset}px` : 0,
                        left: hasBlur ? `-${blurOffset}px` : 0,
                        right: hasBlur ? `-${blurOffset}px` : 0,
                        bottom: hasBlur ? `-${blurOffset}px` : 0,
                        zIndex: 1,
                        overflow: 'hidden' // ✅ Verhindert Überlauf
                    }}>
                        <img 
                            src={isMobileLayout && data.mobileBackgroundImage ? data.mobileBackgroundImage : (data.backgroundImage || defaultBackgroundImage)}
                            alt="Background"
                            style={{
                                width: hasBlur ? `calc(100% + ${blurOffset * 2}px)` : '100%',
                                height: hasBlur ? `calc(100% + ${blurOffset * 2}px)` : '100%',
                                objectFit: 'cover',
                                opacity: data.backgroundImageOpacity > 1 ? 
                                    (parseFloat(data.backgroundImageOpacity) / 100) : // Prozent-Wert (10-100) zu Dezimal
                                    (parseFloat(data.backgroundImageOpacity) || 0.8), // Bereits Dezimal-Wert (0.1-1.0)
                                filter: data.imageEffect ? getImageEffect(data.imageEffect, blurValue).filter : (hasBlur ? `blur(${blurValue}px)` : 'none')
                            }}
                        />
                        
                        {/* ✅ UPDATED: Gradient/Effect Overlay für Vignette-Effects */}
                        {data.imageEffect && getImageEffect(data.imageEffect, blurValue).gradient && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: getImageEffect(data.imageEffect, blurValue).gradient
                            }} />
                        )}
                    </div>
                );
            })()}

            {/* Content Container - Neues zentriertes Layout */}
            <div 
                className={data.enableAnimations && isEditMode ? `simplistic-animated ${data.animationType || 'fadeInUp'}` : ''}
                style={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: isMobileLayout ? '40px 20px' : '80px 40px',
                    textAlign: 'center',
                    flex: '1',
                    width: '100%',
                    height: '100%',
                    gap: layoutConfig.centerLayout ? '24px' : '16px',
                    animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : undefined,
                    animationDelay: data.enableAnimations && isEditMode ? `${data.animationDelay || 200}ms` : undefined
                }}
                key={animationKey}
            >
                
                {/* Icon - Über dem Titel bei zentriertem Layout */}
                {data.showIcon && data.iconImage && layoutConfig.centerLayout && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '16px'
                    }}>
                        <img 
                            src={data.iconImage}
                            alt="Icon"
                            style={{
                                height: appendPx(data.iconHeight || 80),
                                width: 'auto',
                                objectFit: 'contain',
                                filter: [
                                    data.iconShadow ? 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' : '',
                                    data.iconInvert ? 'invert(1)' : ''
                                ].filter(Boolean).join(' ') || 'none'
                            }}
                        />
                    </div>
                )}
                
                {/* Main Title - Conditional Inline-Editing basierend auf Highlights */}
                {data.showTitleText !== false && (
                    <div style={{ margin: '0 20px' }}>
                        {/* ✅ CLEAN CONDITION: Inline-Editing nur wenn KEINE Highlights aktiv */}
                        {(!data.titleHighlightWords || data.titleHighlightWords.trim() === '') ? (
                            // 🔧 NORMALE INLINE-EDITING (ohne Highlights)
                            renderEditableText(
                                'titleText',
                                data.titleText,
                                'h1',
                                {
                                    ...((!data.titleEffect || data.titleEffect === 'none') && {
                                        color: data.titleColor || '#ffffff'
                                    }),
                                    fontSize: appendPx(getResponsiveSize('titleSize')),
                                    fontWeight: data.titleWeight || '800',
                                    fontFamily: data.titleFont || fonts.globalBigFont,
                                    margin: '0',
                                    lineHeight: '1.1',
                                    letterSpacing: '-0.02em',
                                    textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
                                    textAlign: 'center',
                                    ...getTextEffectStyles(data.titleEffect)
                                },
                                'Enter your main title...'
                            )
                        ) : (
                            // 🎨 HIGHLIGHT-MODUS (nur Anzeige, kein Inline-Editing)
                            <h1 style={{
                                ...((!data.titleEffect || data.titleEffect === 'none') && {
                                    color: data.titleColor || '#ffffff'
                                }),
                                fontSize: appendPx(getResponsiveSize('titleSize')),
                                fontWeight: data.titleWeight || '800',
                                fontFamily: data.titleFont || fonts.globalBigFont,
                                margin: '0',
                                lineHeight: '1.1',
                                letterSpacing: '-0.02em',
                                textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
                                textAlign: 'center',
                                whiteSpace: 'pre-wrap',
                                ...getTextEffectStyles(data.titleEffect)
                            }}>
                                <HighlightText
                                    text={data.titleText}
                                    highlightWords={data.titleHighlightWords}
                                    baseStyle={{}}
                                    highlightFont={data.titleHighlightFont || fonts.globalSmallFont}
                                    highlightColor={data.titleHighlightColor}
                                    highlightWeight={data.titleHighlightWeight}
                                highlightSize={data.titleHighlightSize || data.titleSize || 92}
                                highlightSizeMobile={data.titleHighlightSizeMobile || data.titleSizeMobile || 48}
                                highlightSizeBig={data.titleHighlightSizeBig || data.titleSizeBig || 104}
                                highlightMarginLeft={data.titleHighlightMarginLeft || 0}
                                highlightMarginRight={data.titleHighlightMarginRight || 0}
                                highlightEffect={data.titleHighlightEffect}
                                    getTextEffectStyles={getTextEffectStyles}
                                />
                            </h1>
                        )}
                    </div>
                )}

                {/* Subtitle - Inline Editable - KLEIN und dezent */}
                {data.showSubtitleText !== false && renderEditableText(
                    'subtitleText',
                    data.subtitleText,
                    'p',
                    {
                        ...((!data.subtitleEffect || data.subtitleEffect === 'none') && {
                            color: data.subtitleColor || '#f0f0f0'
                        }),
                        fontSize: appendPx(getResponsiveSize('subtitleSize')),
                        fontWeight: data.subtitleWeight || '300',
                        fontFamily: data.subtitleFont || fonts.globalSmallFont,
                        marginBottom: '0',
                        lineHeight: '1.5',
                        textShadow: data.textShadow ? '0 1px 3px rgba(0, 0, 0, 0.3)' : 'none',
                        textAlign: 'center',
                        maxWidth: '600px',
                        opacity: '0.9',
                        ...getTextEffectStyles(data.subtitleEffect)
                    },
                    'Enter subtitle text...'
                )}

                {/* CTA Button - Inline Editable - Zentriert */}
                {data.showButtonText !== false && renderEditableText(
                    'buttonText',
                    data.buttonText,
                    'button',
                    {
                        // 🎨 GRADIENT vs. SOLID COLOR
                        background: data.buttonGradientEnabled 
                            ? `linear-gradient(135deg, ${data.buttonGradientColor1 || '#667eea'} 0%, ${data.buttonGradientColor2 || '#764ba2'} 100%)`
                            : (data.buttonColor || '#D4A574'),
                            
                        // 🎬 ANIMATIONS
                        backgroundSize: data.buttonGradientEnabled && data.buttonGradientAnimation ? '300% 300%' : 'auto',
                        animation: data.buttonGradientEnabled && data.buttonGradientAnimation 
                            ? 'gradientShift 4s ease-in-out infinite' 
                            : data.buttonAnimationsEnabled && data.buttonAnimationType && data.buttonAnimationType !== 'none'
                            ? `${data.buttonAnimationType} ${data.buttonAnimationSpeed || 2000}ms ease-in-out infinite`
                            : 'none',
                            
                        // 🎨 COLORS & STYLING
                        color: data.buttonTextColor || '#FFFFFF',
                        
                        // 🔲 BORDER
                        border: data.buttonBorderEnabled && data.buttonBorderWidth && data.buttonBorderWidth > 0
                            ? `${data.buttonBorderWidth}px solid ${data.buttonBorderColor || '#D4A574'}`
                            : 'none',
                            
                        // 🌟 SHADOW
                        boxShadow: data.buttonShadowEnabled 
                            ? `0 ${data.buttonShadowDistance || 8}px ${data.buttonShadowIntensity || 15}px ${data.buttonShadowColor || '#000000'}${Math.round((data.buttonShadowOpacity || 25) * 2.55).toString(16).padStart(2, '0')}`
                            : '0 4px 12px rgba(0, 0, 0, 0.15)',
                            
                        // 📐 RESPONSIVE DIMENSIONS
                        fontSize: appendPx(getResponsiveSize('buttonTextSize')),
                        fontWeight: isMobileLayout 
                            ? (data.buttonTextWeightMobile || data.buttonTextWeight || '600') 
                            : (data.buttonTextWeight || '600'),
                        fontFamily: data.buttonFont || fonts.globalSmallFont,
                        width: 'auto',
                        minWidth: isMobileLayout ? '200px' : '250px',
                        maxWidth: isMobileLayout 
                            ? appendPx(data.buttonMaxWidthMobile || data.buttonMaxWidth || 300) 
                            : appendPx(data.buttonMaxWidth || 350),
                        height: isMobileLayout 
                            ? appendPx(data.buttonHeightMobile || data.buttonHeight || 50) 
                            : appendPx(data.buttonHeight || 54),
                        borderRadius: isMobileLayout 
                            ? appendPx(data.buttonBorderRadiusMobile ?? data.buttonBorderRadius ?? 8) 
                            : appendPx(data.buttonBorderRadius ?? 8),
                            
                        // 🎯 LAYOUT PROPERTIES - Zentriert
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        textDecoration: 'none',
                        outline: 'none',
                        alignSelf: 'center',
                        padding: '0 32px'
                    },
                    'Enter button text...'
                )}
            </div>
            </div>
        </>
    );
}

export default SimplisticHeader;
